import { z } from "zod";
import { REGIONS } from "@typedoc/contracts/regions";
import { AUTH_API_BASE } from "./auth-api-base";
import { readApiResponse } from "./app-error";
import { boundedRequest } from "./bounded-request";
import { createOnboardingClient, type OnboardingClient } from "./rpc/onboarding";

const organisationSchema = z.object({
  name: z.string().min(1),
  domain: z.string().min(1),
  memberCount: z.number().int().nonnegative(),
  workspaceCount: z.number().int().nonnegative(),
  region: z.enum(REGIONS),
  approvers: z.array(z.string().min(1)),
});

/**
 * Strict, and deliberately stripping: the service must not send an organisation id to someone
 * who is not a member, and parsing here means one could never reach a component even if it did.
 */
export const onboardingStateSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("done") }),
  z.object({ state: z.literal("create"), domain: z.string().min(1).nullable() }),
  z.object({ state: z.literal("join"), organisation: organisationSchema }),
  z.object({
    state: z.literal("requested"),
    organisation: organisationSchema,
    requestedAt: z.iso.datetime(),
  }),
]);
export type OnboardingState = z.infer<typeof onboardingStateSchema>;

// Same base the auth service's other browser clients use (see `auth-client.ts`'s
// `authPublicClient`): the onboarding routes are mounted in that service too, but as their own
// Hono sub-app, so they need their own typed client rather than reusing that one.
export const onboardingClient = createOnboardingClient(AUTH_API_BASE);

export async function fetchOnboardingState(
  signal: AbortSignal,
  timeoutMs = 5000,
): Promise<OnboardingState> {
  return boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await onboardingClient.onboarding.state.$get(undefined, {
          init: { signal: requestSignal },
        }),
        onboardingStateSchema,
      ),
    signal,
    timeoutMs,
  );
}

/** The two roles the service's invitation input accepts. */
export type InviteRole = "member" | "admin";

export type CreateOrganisationRequest = {
  name: string;
  invites: { email: string; role: InviteRole }[];
  /**
   * Whether this person asked for an organisation of their own rather than the one their email
   * domain matches. The service derives the domain from the session and answers `CONFLICT` when
   * a match exists, so today this only ever arrives false — it is carried in the request because
   * that is where the intent belongs, not decided again on the way out.
   */
  separate: boolean;
};

/**
 * Deliberately lenient about `invitesFailed`'s contents: the addresses come back exactly as they
 * were sent, and a stricter parse here would turn a created organisation into "we couldn't read
 * the service response" — the one answer that would be untrue.
 */
export const createOrganisationResultSchema = z.object({
  organisationName: z.string().min(1),
  workspaceName: z.string().min(1),
  invitesSent: z.number().int().nonnegative(),
  invitesFailed: z.array(z.string().min(1)),
});
export type CreateOrganisationResult = z.infer<typeof createOrganisationResultSchema>;

export async function createOrganisation(
  input: CreateOrganisationRequest,
  signal: AbortSignal,
  timeoutMs = 10000,
  // Injectable the way the RPC clients take a fetcher, so the request this
  // builds and the response it parses can both be exercised against the real client.
  client: OnboardingClient = onboardingClient,
): Promise<CreateOrganisationResult> {
  return boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await client.onboarding.organisation.$post(
          { json: input },
          { init: { signal: requestSignal } },
        ),
        createOrganisationResultSchema,
      ),
    signal,
    timeoutMs,
  );
}

/**
 * Both join-request endpoints answer with this. `requestedAt` is when the request was made and
 * a resend deliberately does not move it, so this is the same answer either call gives.
 */
export const joinRequestResultSchema = z.object({
  requestedAt: z.iso.datetime(),
  notified: z.number().int().nonnegative(),
});
export type JoinRequestResult = z.infer<typeof joinRequestResultSchema>;

/**
 * Asks to join the organisation this session's own email domain matches. Deliberately sends no
 * body: the service re-derives the match from the session, so nobody can aim a request at an
 * organisation they were never shown — and there is therefore nothing here to get wrong.
 */
export async function requestToJoin(
  signal: AbortSignal,
  timeoutMs = 10000,
  client: OnboardingClient = onboardingClient,
): Promise<JoinRequestResult> {
  return boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await client.onboarding["join-request"].$post(undefined, {
          init: { signal: requestSignal },
        }),
        joinRequestResultSchema,
      ),
    signal,
    timeoutMs,
  );
}

/** Emails the approvers about the request that already exists. Rate limited by the service. */
export async function resendJoinRequest(
  signal: AbortSignal,
  timeoutMs = 10000,
  client: OnboardingClient = onboardingClient,
): Promise<JoinRequestResult> {
  return boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await client.onboarding["join-request"].resend.$post(undefined, {
          init: { signal: requestSignal },
        }),
        joinRequestResultSchema,
      ),
    signal,
    timeoutMs,
  );
}
